import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin, Sparkles } from "lucide-react";
import SectionHeading from "../components/ui/SectionHeading.jsx";
import CTABanner from "../components/ui/CTABanner.jsx";
import LocationCard from "../components/cards/LocationCard.jsx";
import Marquee from "../components/ui/Marquee.jsx";
import { Reveal, MaskLines, ImageReveal } from "../components/motion/Reveal.jsx";
import useSeo from "../hooks/useSeo.js";
import { CLUBS, FACILITIES, CONTACT, KATARGAM_MAP_URL, ADAJAN_MAP_URL, VARACHHA_MAP_URL } from "../content.js";

const LIFESTYLES = [
  { title: "For Performance", copy: "For athletes and serious fitness enthusiasts — elite conditioning zones, strength development and professional coaching.", img: "/oneiros/strongest-gym.jpg", to: "/clubs/katargam" },
  { title: "For Wellness", copy: "For movement, recovery and everyday health — studios, pools and recovery spaces designed around how you live.", img: "/oneiros/adajan-swimming.jpg", to: "/clubs/adajan" },
  { title: "For Families", copy: "For active family lifestyles — kids arenas, game identification programs and spaces where every generation belongs.", img: "/oneiros/adajan-kids-arena.jpg", to: "/blue-membership" },
];

function Hero() {
  return (
    <section className="mx-auto max-w-[1440px] px-6 pb-16 pt-36 lg:px-12 lg:pt-48">
      <SectionHeading
        eyebrow="Our Clubs"
        lines={["Three Destinations.", "One Vision."]}
        copy="Three unique Oneiros destinations designed around fitness, sports, wellness, family and community."
      />
    </section>
  );
}

function DestinationRows() {
  return (
    <section className="mx-auto flex max-w-[1440px] flex-col gap-24 px-6 py-16 lg:px-12 lg:py-24">
      {CLUBS.map((club, i) => (
        <article key={club.id} data-testid={`clubs-row-${club.id}`} className={`grid items-center gap-12 lg:grid-cols-12 lg:gap-16`}>
          <ImageReveal
            src={club.image}
            alt={`Oneiros ${club.shortName} — ${club.positioning}`}
            className={`lg:col-span-7 aspect-[16/11] w-full ${i % 2 === 1 ? "lg:order-2" : ""}`}
          />
          <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
            <Reveal>
              <p className="eyebrow flex items-center gap-3">
                <span className="font-display text-3xl text-brand">{club.index}</span>
                <span className="inline-block h-[2px] w-8 bg-brand" />
                {club.eyebrow}
              </p>
            </Reveal>
            <MaskLines lines={[club.name]} className="mt-5 font-display text-4xl text-navy sm:text-5xl" />
            <Reveal delay={0.1}>
              <p className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-brand">{club.positioning}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-5 max-w-md text-base leading-relaxed text-mute md:text-lg">{club.description}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-[10px] font-bold uppercase tracking-[0.25em] text-faint">{club.uspLabel}</p>
              <ul className="mt-3 flex flex-wrap gap-2">
                {club.highlights.map((h, hi) => (
                  <li
                    key={h}
                    data-testid={`clubs-usp-${club.id}-${hi}`}
                    className={
                      hi === 0
                        ? "flex items-center gap-1.5 border border-brand/50 bg-brand-tint px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-brand"
                        : "border border-hairline px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-navy/70"
                    }
                  >
                    {hi === 0 && <Sparkles className="h-3 w-3 shrink-0" />}
                    {h}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.25}>
              <Link
                data-testid={`clubs-explore-${club.id}`}
                to={`/clubs/${club.id}`}
                className="group mt-8 inline-flex items-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300"
              >
                Explore {club.shortName}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </article>
      ))}
    </section>
  );
}

function FindYourOneiros() {
  return (
    <section className="grain relative overflow-hidden bg-navy py-24 text-white lg:py-32">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <SectionHeading eyebrow="Find Your Oneiros" lines={["Every Goal Has", "A Home Here."]} dark />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {LIFESTYLES.map((l, i) => (
            <Reveal key={l.title} delay={i * 0.1}>
              <Link data-testid={`lifestyle-card-${i}`} to={l.to} className="group relative block overflow-hidden">
                <img src={l.img} alt={l.title} loading="lazy" className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-navy/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-8">
                  <p className="font-display text-2xl text-white md:text-3xl">{l.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-white/70">{l.copy}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-brand">
                    Explore <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const MAP_PINS = [
  { name: "Katargam", tag: "Sports Excellence", url: KATARGAM_MAP_URL, pos: "right-[14%] top-[12%]", testId: "clubs-map-pin-katargam" },
  { name: "Adajan", tag: "Premium Family Club", url: ADAJAN_MAP_URL, pos: "left-[16%] top-[44%]", testId: "clubs-map-pin-adajan" },
  { name: "Varachha", tag: "A New Chapter", url: VARACHHA_MAP_URL, pos: "right-[22%] bottom-[12%]", testId: "clubs-map-pin-varachha" },
];

function LocationsSection() {
  return (
    <section data-testid="clubs-map-section" className="grain relative overflow-hidden bg-navy py-24 text-white lg:py-32">
      <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-12">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="Locations"
            lines={["Different", "Neighbourhoods.", "A Stronger Surat."]}
            dark
            copy="Three unique locations. One connected community. Experience the same world-class standards across all Oneiros clubs."
          />
          <Reveal delay={0.2}>
            <p className="mt-8 flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">
              <MapPin className="h-4 w-4 text-brand" /> One Surat · One Oneiros
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1} className="relative aspect-[16/10] overflow-hidden border border-white/10 bg-navy-card">
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 800 500" preserveAspectRatio="none" aria-hidden>
              <defs>
                <linearGradient id="river" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#2E5B8F" />
                  <stop offset="100%" stopColor="#3A6FA8" />
                </linearGradient>
                <radialGradient id="glow" cx="50%" cy="50%" r="70%">
                  <stop offset="0%" stopColor="#1E3A5F" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#0F2038" stopOpacity="1" />
                </radialGradient>
              </defs>
              <rect width="800" height="500" fill="#13294A" />
              <rect width="800" height="500" fill="url(#glow)" />
              {/* city blocks */}
              {[[40,40,150,90],[210,30,120,110],[60,160,130,110],[230,170,90,80],[560,60,180,100],[350,70,160,80],[620,300,140,120],[80,320,160,120],[300,360,140,90],[460,150,120,110]].map(([x,y,w,h],i)=>(
                <rect key={`b${i}`} x={x} y={y} width={w} height={h} rx="6" fill="#1E3A5F" opacity="0.55" stroke="#5B7BA6" strokeOpacity="0.25" />
              ))}
              {/* river */}
              <path d="M0,255 C160,205 330,330 490,278 C630,232 730,300 800,262 L800,340 C700,375 560,360 420,360 C260,360 120,320 0,330 Z" fill="url(#river)" opacity="0.85" />
              <path d="M0,255 C160,205 330,330 490,278 C630,232 730,300 800,262" stroke="#7FA8D4" strokeOpacity="0.5" strokeWidth="2.5" fill="none" />
              {/* main roads */}
              <path d="M0,120 L800,95" stroke="#8FA8C6" strokeOpacity="0.35" strokeWidth="5" />
              <path d="M0,420 L800,395" stroke="#8FA8C6" strokeOpacity="0.35" strokeWidth="5" />
              <path d="M210,0 L240,500" stroke="#8FA8C6" strokeOpacity="0.3" strokeWidth="4" />
              <path d="M560,0 L530,500" stroke="#8FA8C6" strokeOpacity="0.3" strokeWidth="4" />
              <path d="M0,300 C200,280 400,330 800,300" stroke="#F0561D" strokeOpacity="0.3" strokeWidth="3" fill="none" strokeDasharray="10 8" />
            </svg>
            {MAP_PINS.map((p) => (
              <a
                key={p.name}
                data-testid={p.testId}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`group absolute z-10 flex flex-col items-center ${p.pos}`}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand shadow-[0_0_0_8px_rgba(240,86,29,0.18)] transition-transform duration-300 group-hover:scale-110">
                  <MapPin className="h-4 w-4 text-white" fill="currentColor" strokeWidth={1} />
                </span>
                <span className="mt-2 whitespace-nowrap bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-navy shadow-lg">
                  {p.name} <span className="text-brand">·</span> {p.tag}
                </span>
              </a>
            ))}
          </Reveal>
        </div>
      </div>
      <div className="mx-auto mt-16 grid max-w-[1440px] gap-6 px-6 md:grid-cols-3 lg:px-12">
        {CONTACT.clubs.map((club, i) => (
          <LocationCard key={club.id} club={{ ...club, index: String(i + 1).padStart(2, "0"), name: `Oneiros ${club.name}` }} testId={`clubs-location-${club.id}`} />
        ))}
      </div>
    </section>
  );
}

function Ecosystem() {
  return (
    <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
      <SectionHeading eyebrow="The Ecosystem" lines={["World-Class Facilities", "Across Oneiros."]} />
      <div className="mt-12 grid grid-cols-2 gap-3 md:grid-cols-4">
        {FACILITIES.slice(0, 12).map((f) => (
          <Reveal key={f.id}>
            <div data-testid={`ecosystem-facility-${f.id}`} className="border border-hairline px-4 py-4 text-center text-xs font-semibold uppercase tracking-[0.15em] text-navy/75 transition-colors hover:border-brand hover:text-brand">
              {f.name}
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function OurClubs() {
  useSeo({
    title: "Our Clubs in Surat | Katargam, Adajan & Varachha — Oneiros Sports Club",
    description:
      "Explore Oneiros Sports Club destinations in Surat — Katargam, Adajan and Varachha. Premium fitness, sports, wellness and family experiences under one Oneiros standard.",
    image: "/oneiros/katargam-ext.jpg",
  });
  return (
    <>
      <Hero />
      <DestinationRows />
      <FindYourOneiros />
      <LocationsSection />
      <Marquee items={["Katargam", "Adajan", "Varachha", "One Membership", "Whole Family"]} slow />
      <Ecosystem />
      <CTABanner
        eyebrow="Join Oneiros"
        lines={["Find Your Club.", "Find Your Rhythm."]}
        primary={{ label: "Enquire Now", to: "/contact" }}
        secondary={{ label: "Explore Blue Membership", to: "/blue-membership" }}
        testId="clubs-final-cta"
      />
    </>
  );
}
